/*!
 * StudiFi calc engine — Income Tax + National Insurance
 *
 * MIT when lifted. Pure functions. No I/O.
 *
 * Handles England/Wales/NI and Scotland bands separately because
 * Scottish rates diverge materially.
 */

/**
 * Compute the effective personal allowance after the £100k–£125,140
 * taper (£1 of allowance lost per £2 of income over £100,000).
 *
 * @param {number} salary
 * @param {object} band  taxData.englandNIWales OR taxData.scotland
 * @returns {number}
 */
export function effectivePersonalAllowance(salary, band) {
  const { personalAllowance, personalAllowanceTaperStart } = band;
  const overTaper = Math.max(0, salary - personalAllowanceTaperStart);
  return Math.max(0, personalAllowance - overTaper / 2);
}

/**
 * Compute income tax on a given salary.
 *
 * The taper is handled by shrinking the personal allowance and
 * folding the difference into the basic-rate band (mirrors how
 * PAYE actually works).
 *
 * @param {number} salary
 * @param {object} band     taxData.englandNIWales OR taxData.scotland
 * @returns {number}        Annual income tax (£, unrounded)
 */
export function incomeTax(salary, band) {
  const pa = effectivePersonalAllowance(salary, band);
  const taxable = Math.max(0, salary - pa);

  let tax = 0;
  let previousCap = pa;
  for (const bracket of band.bands) {
    if (bracket.rate === 0) continue;
    const bracketTop = bracket.upTo ?? Infinity;
    const bracketAmount = Math.max(0, Math.min(salary, bracketTop) - previousCap);
    tax += bracketAmount * bracket.rate;
    previousCap = bracketTop;
    if (salary <= bracketTop) break;
  }
  return tax;
}

/**
 * Compute Class 1 primary (employee) National Insurance.
 *
 * @param {number} salary
 * @param {object} niData  nationalInsurance.class1Employee
 * @returns {number}       Annual NI (£, unrounded)
 */
export function nationalInsurance(salary, niData) {
  const { primaryThreshold: PT, upperEarningsLimit: UEL, mainRate, upperRate } = niData;
  if (salary <= PT) return 0;
  const inMainBand = Math.min(salary, UEL) - PT;
  const overUEL = Math.max(0, salary - UEL);
  return inMainBand * mainRate + overUEL * upperRate;
}

/**
 * Compute take-home breakdown.
 *
 * @param {object} params
 * @param {number} params.salary
 * @param {object} params.band            taxData.englandNIWales OR taxData.scotland
 * @param {object} params.niData          nationalInsurance.class1Employee
 * @param {function} [params.slcRepayment] Optional (salary) => annual SLC (£)
 * @param {number} [params.pensionSalarySacrifice] Employee pension via
 *   salary sacrifice (£, subtracted from salary BEFORE tax + NI)
 * @returns {{
 *   gross: number,
 *   pensionSacrifice: number,
 *   taxableSalary: number,
 *   tax: number,
 *   ni: number,
 *   slc: number,
 *   net: number
 * }}
 */
export function takeHome(params) {
  const {
    salary,
    band,
    niData,
    slcRepayment = () => 0,
    pensionSalarySacrifice = 0,
  } = params;

  const gross = salary;
  const taxableSalary = Math.max(0, gross - pensionSalarySacrifice);
  const tax = incomeTax(taxableSalary, band);
  const ni = nationalInsurance(taxableSalary, niData);
  const slc = slcRepayment(taxableSalary);
  const net = taxableSalary - tax - ni - slc;

  return {
    gross,
    pensionSacrifice: pensionSalarySacrifice,
    taxableSalary,
    tax,
    ni,
    slc,
    net,
  };
}
